import { useIntl } from '@faceup/localization'
import { trpc, useQuery } from '@faceup/trpc'
import { Button, List, Modal } from '@faceup/ui-base'
import type { AiPromptName } from '@faceup/utils'
import { useState } from 'react'
import { AiPromptPreviewModal } from './AiPromptPreviewModal'
import { aiPromptDefinitions, hasAiPromptPreview } from './aiPromptDefinitions'

type AiPromptHistoryModalProps = {
  name: AiPromptName
  onClose: () => void
}

export const AiPromptHistoryModal = ({ name, onClose }: AiPromptHistoryModalProps) => {
  const { formatDate } = useIntl()
  const { data: history, isLoading } = useQuery(trpc.aiPrompt.history.queryOptions({ name }))
  const [viewed, setViewed] = useState<{ content: string; title: string } | null>(null)
  const { label } = aiPromptDefinitions[name]
  const hasPreview = hasAiPromptPreview(name)

  return (
    <Modal
      title={`History - ${label}`}
      open
      onClose={onClose}
      width={800}
      footer={<Button onClick={onClose}>Close</Button>}
    >
      <List
        dataSource={history ?? []}
        loading={isLoading}
        renderItem={version => {
          const createdAt = formatDate(version.createdAt, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
          return (
            <List.Item
              actions={[
                <Button
                  key='view'
                  type='link'
                  onClick={() => setViewed({ content: version.content, title: createdAt })}
                >
                  {hasPreview ? 'Preview & test' : 'View'}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={createdAt}
                description={`${version.note ?? 'No note'} · ${version.content.length} characters`}
              />
            </List.Item>
          )
        }}
      />

      {hasPreview ? (
        <AiPromptPreviewModal
          name={name}
          content={viewed?.content ?? null}
          title={viewed?.title ?? ''}
          onClose={() => setViewed(null)}
        />
      ) : (
        <Modal
          title={viewed?.title}
          open={viewed !== null}
          onClose={() => setViewed(null)}
          width={1000}
          footer={<Button onClick={() => setViewed(null)}>Close</Button>}
        >
          <pre className='max-h-[60vh] overflow-y-auto whitespace-pre-wrap break-words rounded-xl border border-colorBorder p-4 font-mono'>
            {viewed?.content}
          </pre>
        </Modal>
      )}
    </Modal>
  )
}
